import { DamacAPI } from "@utils/damac/api";
import { useSession } from "next-auth/react";
import { useFormatter, useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import React, { memo, useContext, useEffect, useMemo, useState } from "react";

import { search } from "@/actions/currencyConversion";
import Icon from "@/components/ui/Icon/Icon";
import { ModalsContext } from "@/context/ModalsContext";
import { ctaClickTracking, mortgageCalculatorDataLayerEvent } from "@/dataLayer/dataLayer";

import Button, { ButtonSize, ButtonTheme } from "../ui/Button/Button";
import MortgageCalculatorLabelAndIcon from "./MortgageCalculatorLabelAndIcon";

const Modal = dynamic(() => import("../ui/Modal/Modal"), { ssr: false });
const ModalContent = dynamic(() => import("../ui/Modal/ModalContent"), { ssr: false });

interface Props {
  label: string;
  icon: string;
  mortgageCalculatorData: any;
  [key: string]: any;
  locale: string;
  currentCurrencyConverstionRate: number;
}

const MortgageCalculatorLoanAmountSection: React.FC<Props> = ({ label = "", icon, mortgageCalculatorData, currentCurrency, locale, ...data }) => {
  const t = useTranslations();

  const { data: session } = useSession();

  const { setEnquireModalOpen } = useContext(ModalsContext);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const format = useFormatter();
  const {
    propertyPrice,
    loanPeriod,
    downPayment,
    interestRate,
    monthlyPaymentAmount,
    loanPeriodInMonths,
    ldFees,
    registerFees,
    mortgageRegFees,
    evaluationFees,
    downpaymentAmount,
    currentCurrencyConverstionRate,
  } = data;

  const monthlyPaymentFormatedPrice = format.number(Number(Math.ceil(monthlyPaymentAmount * currentCurrencyConverstionRate)));
  const landFeeFormatedPrice = format.number(Number(Math.ceil(ldFees * currentCurrencyConverstionRate)));
  const registerFeesFormatedPrice = format.number(Number(Math.ceil(registerFees * currentCurrencyConverstionRate)));
  const mortgageRegFeesFormatedPrice = format.number(Number(Math.ceil(mortgageRegFees * currentCurrencyConverstionRate)));
  const evaluationFeesFormatedPrice = format.number(Number(Math.ceil(evaluationFees * currentCurrencyConverstionRate)));
  const enquirenowButtonText = t("mortgage.button.enquirenow");
  const downpaymentFormatedAmount = format.number(Number(Math.ceil(downpaymentAmount * currentCurrencyConverstionRate)));
  const getFormattedPrice = (key: string) => {
    switch (key) {
      case "landDepartment":
        return landFeeFormatedPrice;
      case "registerFee":
        return registerFeesFormatedPrice;
      case "evolutionFees":
        return evaluationFeesFormatedPrice;
      case "mortgageRegistrationFees":
        return mortgageRegFeesFormatedPrice;
      default:
        return null;
    }
  };

  const onClikOpenModal = () => {
    setModalOpen(true);
    ctaClickTracking(
      mortgageCalculatorData?.loanAmountSection?.[1]?.title || "",
      "MortgageCalculatorLoanAmountSection",
      "MortgageCalculatorLoanAmountSection",
    );
  };

  const onClickCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      <div className="bg-gray-7 p-l md:w-1/4 w-full">
        <div className="flex items-center justify-between mb-xl">
          <MortgageCalculatorLabelAndIcon label={mortgageCalculatorData?.loanAmountSection?.[0]?.title} icon={icon} />
          <div className="flex items-center justify-center">
            <Button className="text-s font-secondary font-medium decoration-1" onClick={onClikOpenModal}>
              {mortgageCalculatorData?.loanAmountSection?.[1]?.title}
            </Button>
          </div>
        </div>
        <div className="mb-xl">
          <div className="font-primary uppercase mb-xxs text-center font-medium">
            {currentCurrency} {` `}
            {currentCurrencyConverstionRate ? monthlyPaymentFormatedPrice : t("common.loading")}
          </div>
          <div className="font-secondary text-s text-center">
            {t("mortgage.label.permonthfor")} {loanPeriodInMonths} {t("mortgage.label.months")}
          </div>
        </div>
        <div>
          <div className="font-secondary text-s text-center">{mortgageCalculatorData.fields[2]?.title}</div>
          <div className="font-primary uppercase mb-xxs text-center font-medium">
            {currentCurrency}
            {` `}
            {currentCurrencyConverstionRate ? downpaymentFormatedAmount : t("common.loading")}
          </div>
        </div>
        <div className="mt-xl">
          <Button
            customClass="mb-s w-full"
            size={ButtonSize.MEDIUM}
            theme={ButtonTheme.BLACK}
            onClick={() => {
              setEnquireModalOpen(true);
              mortgageCalculatorDataLayerEvent({
                cta_name: t("unit.reserve.now"),
                property_price: propertyPrice,
                loan_period: loanPeriod,
                down_payment: downPayment,
                interest_rate: interestRate,
                loan_amount: monthlyPaymentFormatedPrice,
                land_deparment_fees: landFeeFormatedPrice,
                register_fees: registerFeesFormatedPrice,
                mortgage_registeration_fee: mortgageRegFeesFormatedPrice,
                evolution_fees: evaluationFeesFormatedPrice,
                currency: currentCurrency,
                cta_location: "mortgage calculator",
                title_text: "Mortgage calculator",
                user_logged_in: !!session,
                // user_id: session?.user.id || "",
              });
            }}
          >
            {enquirenowButtonText}
          </Button>
          {/* <Button customClass="mb-s w-full" size={ButtonSize.MEDIUM} theme={ButtonTheme.WHITE}>
            buying partner
          </Button> */}
        </div>
      </div>
      <Modal open={modalOpen} onClose={onClickCloseModal}>
        <ModalContent className="w-full md:w-12\/24 !p-l text-s rounded-lg text-left">
          <div className="grid">
            <div className="mb-xxs flex justify-between">
              <div className="uppercase text-black text-m font-medium">Fee</div>
              <button onClick={onClickCloseModal}>
                <Icon className="stroke-black" name="close-small" size="32" fill="black" title="close-small" />
              </button>
            </div>
            {mortgageCalculatorData?.viewFeesModal?.map((fee: any) => (
              <div key={fee.id} className="border-b border-gray-7 py-l">
                <div className="grid justify-between grid-cols-2 gap-l mb-s">
                  <div className="text-black text-m capitalize">{fee.title}</div>
                  <div className="text-black text-m uppercase text-right">{`${currentCurrency} ${currentCurrencyConverstionRate ? getFormattedPrice(fee.key) : t("common.loading")}`}</div>
                </div>
                <div className="text-black text-s">{fee.description}</div>
              </div>
            ))}
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};

export default memo(MortgageCalculatorLoanAmountSection);
